<template>
	<q-field :error="showValidations" :error-label="describeErrors">
		<q-search :dark="dark" color="secondary" ref="search" :clearable="hasValue" v-model="label" :float-label="labelText" placeholder=" ">
			<q-autocomplete
				v-hack-autocomplete-absolute-position="{ disablePopupBehavior: true }"
				autofocus="true"
				@search="searchCourses"
				@selected="onAutocompleteSelection"
				value-field="label" />
		</q-search>
	</q-field>


</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapMutations } from 'vuex';
import { client as courseAtlasClient } from '@/models/services/courseatlas';
import { CourseEntry, ICourseInfo, IInstitutionInfo } from '@/models';
import config from '@/models/services/config';

@Component({
	methods: mapMutations('wmct', {
		updateLearning: 'UPDATE_LEARNING_COURSE',
	}),
})
export default class CourseAutoComplete extends Vue {
	@Prop() public value!: CourseEntry;

	private get dark() {
		return config.Dark;
	}

	private updateLearning!: ({ learning, selectedCourse}: { learning: CourseEntry, selectedCourse: ICourseInfo}) => void;

	public async searchCourses(terms: string, done: (_: any) => void) {
		const institution  = this.institutionInfo.institutionId;
		if (institution == null) {
			done([]);
			return;
		}

		const courses = await courseAtlasClient.courseSearch({
			search: terms,
			top: 20,
			skip: 0,
			institutionId: institution,
		});

		done(courses.map((course) => ({
			institutionId: course.InstitutionId,
			label: course.FullTitle,
			value: course.CourseId,
			prefix: course.Prefix,
			number: course.Number,
		})));
	}

	private get validating() {
		return this.value.isValidating;
	}

	private get label() {
		return this.value.course.label;
	}

	private set label(val: string | null) {
		const selectedCourse: ICourseInfo = {
			label: val,
		} as ICourseInfo;

		this.updateLearning({ learning: this.value, selectedCourse });
	}

	private onAutocompleteSelection(selectedCourse: ICourseInfo) {
		this.updateLearning({ learning: this.value, selectedCourse });
	}

	private get courseInfo(): ICourseInfo {
		return this.value.course;
	}

	private get institutionInfo(): IInstitutionInfo {
		return this.value.institution;
	}

	private get courseSameAsInstitution() {
		return this.courseInfo.institutionId === this.institutionInfo.institutionId;
	}

	private get hasValue() {
		return this.courseInfo.value != null;
	}

	private get hasEntry() {
		return (this.courseInfo.label || '') !== '';
	}

	private get showValidations() {
		return this.validating && !this.isValid;
	}

	private get isValid() {
		return this.hasEntry === this.hasValue && this.hasValue
			&& this.courseSameAsInstitution;
	}

	private get describeErrors() {
		return 'Please select a course from the list';
	}

	private get labelText() {
		if (this.hasEntry && !this.isValid) {
			return 'Keep typing to refine your results';
		} else {
			return 'Select a course';
		}
	}

	private mounted() {
		if (this.institutionInfo.value && !this.hasEntry) {
			const search = this.$refs.search as Vue;
			const searchInput = search.$refs.input as Vue;
			const formInput = searchInput.$refs.input as HTMLElement;

			formInput.focus();
		}
	}
}
</script>